/**
 * Hosting Subscriptions Hook
 *
 * React hook for managing FluxNode hosting subscriptions with Supabase
 * Provides access to user's subscriptions, nodes, and wallet credentials
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/client';
import type { Database } from '../lib/supabase/types';

type HostingSubscription = Database['public']['Tables']['hosting_subscriptions']['Row'];
type HostingSubscriptionNode = Database['public']['Tables']['hosting_subscription_nodes']['Row'];
type HostingWalletCredential = Database['public']['Tables']['hosting_wallet_credentials']['Row'];

// Extended subscription with nodes and credentials
export interface SubscriptionWithDetails extends HostingSubscription {
  nodes: HostingSubscriptionNode[];
  wallet_credentials: HostingWalletCredential[];
}

export interface UseHostingSubscriptionsReturn {
  // Data
  subscriptions: SubscriptionWithDetails[];
  activeSubscription: SubscriptionWithDetails | null;
  hasActiveSubscription: boolean;

  // Loading states
  loading: boolean;
  error: Error | null;

  // Actions
  refresh: () => Promise<void>;
  updateWalletCredential: (
    subscriptionId: string,
    walletAddress: string,
    updates: { zelId?: string; identityKey?: string }
  ) => Promise<void>;
}

/**
 * Hook for managing FluxNode hosting subscriptions
 *
 * @example
 * ```tsx
 * function HostingDashboard() {
 *   const { subscriptions, activeSubscription, loading } = useHostingSubscriptions();
 *
 *   if (loading) return <Spinner />;
 *
 *   if (!activeSubscription) {
 *     return <div>No active subscription</div>;
 *   }
 *
 *   return (
 *     <div>
 *       <h2>Your Subscription</h2>
 *       <p>Status: {activeSubscription.status}</p>
 *       <p>Nodes: {activeSubscription.nodes.length}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useHostingSubscriptions(): UseHostingSubscriptionsReturn {
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch all subscriptions for current user with related data
   */
  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        // Not authenticated - return empty
        setSubscriptions([]);
        setLoading(false);
        return;
      }

      // Fetch subscriptions (RLS will filter by user_id or email)
      const { data: subs, error: subsError } = await supabase
        .from('hosting_subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (subsError) {
        // Table might not exist yet - return empty
        if (subsError.code === '42P01') {
          console.log('[useHostingSubscriptions] Table does not exist yet');
          setSubscriptions([]);
          setLoading(false);
          return;
        }
        throw subsError;
      }

      if (!subs || subs.length === 0) {
        setSubscriptions([]);
        setLoading(false);
        return;
      }

      // Fetch nodes for all subscriptions
      const subscriptionIds = subs.map((s: HostingSubscription) => s.id);

      const [nodesResult, credentialsResult] = await Promise.all([
        supabase
          .from('hosting_subscription_nodes')
          .select('*')
          .in('subscription_id', subscriptionIds),
        supabase
          .from('hosting_wallet_credentials')
          .select('*')
          .in('subscription_id', subscriptionIds),
      ]);

      // Log errors for debugging
      if (nodesResult.error) {
        console.error('[useHostingSubscriptions] Error fetching nodes:', nodesResult.error);
      }
      if (credentialsResult.error) {
        console.error('[useHostingSubscriptions] Error fetching credentials:', credentialsResult.error);
      }

      const nodes: HostingSubscriptionNode[] = nodesResult.data || [];
      const credentials: HostingWalletCredential[] = credentialsResult.data || [];

      console.log('[useHostingSubscriptions] Fetched', subs.length, 'subscriptions,', nodes.length, 'nodes');

      // Combine data
      const subscriptionsWithDetails: SubscriptionWithDetails[] = subs.map((sub: HostingSubscription) => ({
        ...sub,
        nodes: nodes.filter((n: HostingSubscriptionNode) => n.subscription_id === sub.id),
        wallet_credentials: credentials.filter((c: HostingWalletCredential) => c.subscription_id === sub.id),
      }));

      setSubscriptions(subscriptionsWithDetails);
    } catch (err) {
      console.error('[useHostingSubscriptions] Error:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and subscribe to changes
  useEffect(() => {
    fetchSubscriptions();

    // Subscribe to realtime changes on subscriptions
    const channel = supabase
      .channel('hosting_subscriptions_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'hosting_subscriptions',
        },
        () => {
          // Refetch on any change
          fetchSubscriptions();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'hosting_subscription_nodes',
        },
        () => {
          fetchSubscriptions();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchSubscriptions]);

  /**
   * Get the first active subscription
   */
  const activeSubscription =
    subscriptions.find((s) => s.status === 'active') || null;

  /**
   * Update wallet credentials (Zel ID / Identity Key)
   */
  const updateWalletCredential = async (
    subscriptionId: string,
    walletAddress: string,
    updates: { zelId?: string; identityKey?: string }
  ) => {
    try {
      setError(null);

      // Find existing credential
      const subscription = subscriptions.find((s) => s.id === subscriptionId);
      if (!subscription) {
        throw new Error('Subscription not found');
      }

      const existingCred = subscription.wallet_credentials.find(
        (c) => c.wallet_address === walletAddress
      );

      if (existingCred) {
        // Update existing
        const updateData: Record<string, string> = {};
        if (updates.zelId !== undefined) updateData.zel_id = updates.zelId;
        if (updates.identityKey !== undefined) updateData.identity_key = updates.identityKey;

        const { error: updateError } = await supabase
          .from('hosting_wallet_credentials')
          .update(updateData)
          .eq('id', existingCred.id);

        if (updateError) throw updateError;
      } else {
        // Insert new
        const { error: insertError } = await supabase
          .from('hosting_wallet_credentials')
          .insert({
            subscription_id: subscriptionId,
            wallet_address: walletAddress,
            zel_id: updates.zelId || '',
            identity_key: updates.identityKey || '',
          });

        if (insertError) throw insertError;
      }

      // Refresh data
      await fetchSubscriptions();
    } catch (err) {
      console.error('[useHostingSubscriptions] Error updating credential:', err);
      setError(err as Error);
      throw err;
    }
  };

  return {
    subscriptions,
    activeSubscription,
    hasActiveSubscription: !!activeSubscription,
    loading,
    error,
    refresh: fetchSubscriptions,
    updateWalletCredential,
  };
}
